// src/components/ThemesDropdown.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { standardUserApi } from "../services/standardUserApi";

/**
 * ThemesDropdown - A reusable, eye-catching component for displaying gathering themes
 *
 * Categories are displayed in this order:
 * 1. General Matched Gatherings (casual themes)
 * 2. Industry Gatherings (professional themes)
 * 3. Interest-themed Gatherings (interest_based themes)
 * 4. Countries (country themes)
 */
const ThemesDropdown = ({ showSectionTitle = false, sectionTitle = "View Themes & Book a Gathering", locationId = null }) => {
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [activeThemeIds, setActiveThemeIds] = useState(null); // null = no filter applied
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState('casual'); // Expand first category by default

  // Category configuration with order, titles, and descriptions
  const categoryConfig = [
    {
      type: 'casual',
      title: 'General Gatherings',
      subtitle: 'Meet new people over drinks',
      icon: '🎯',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      iconBg: 'rgba(102, 126, 234, 0.15)',
    },
    {
      type: 'professional',
      title: 'Industry Gatherings',
      subtitle: 'Meet people in your industry',
      icon: '💼',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      iconBg: 'rgba(17, 153, 142, 0.15)',
    },
    {
      type: 'interest_based',
      title: 'Interest-themed Gatherings',
      subtitle: 'Connect over shared passions',
      icon: '🌟',
      gradient: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
      iconBg: 'rgba(252, 74, 26, 0.15)',
    },
    {
      type: 'country',
      title: 'Countries',
      subtitle: 'Meet people from around the world',
      icon: '🌍',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      iconBg: 'rgba(79, 172, 254, 0.15)',
    },
  ];

  useEffect(() => {
    const loadThemes = async () => {
      try {
        const [lookupData, activeIds] = await Promise.all([
          standardUserApi.getLookupData(),
          locationId ? standardUserApi.getActiveThemeIds(locationId) : Promise.resolve(null),
        ]);
        setThemes(lookupData.themes || []);
        setActiveThemeIds(activeIds);
      } catch (error) {
        console.error("Failed to load themes:", error);
      } finally {
        setLoading(false);
      }
    };
    loadThemes();
  }, [locationId]);

  // Group themes by type, filtered by active location if known
  const getThemesByType = (type) => {
    return themes.filter(theme =>
      theme.theme_type === type &&
      (activeThemeIds === null || activeThemeIds.includes(theme.id))
    );
  };

  const handleThemeClick = (theme) => {
    // Store selected theme in sessionStorage
    sessionStorage.setItem('selectedThemeId', theme.id);
    sessionStorage.setItem('selectedThemeName', theme.theme_name);
    sessionStorage.setItem('selectedThemeType', theme.theme_type);
    sessionStorage.setItem('selectedThemeWeekly', theme.weekly ? 'true' : 'false');

    // Navigate to theme landing page if available, otherwise fall back to gathering matcher
    if (theme.landing_page_slug) {
      navigate(`/theme/${theme.landing_page_slug}`);
    } else {
      navigate('/gathering-matcher');
    }
  };

  const toggleCategory = (categoryType) => {
    setExpandedCategory(expandedCategory === categoryType ? null : categoryType);
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {showSectionTitle && (
          <div
            style={{
              height: '36px',
              backgroundColor: '#f0f0f0',
              borderRadius: '8px',
              marginBottom: '24px',
              maxWidth: '400px',
              margin: '0 auto 24px auto',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: '2px solid transparent',
              }}
            >
              {/* Skeleton Category Header */}
              <div
                style={{
                  padding: '20px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                {/* Icon skeleton */}
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    backgroundColor: '#e0e0e0',
                    flexShrink: 0,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${index * 0.1}s`,
                  }}
                />
                {/* Text content skeleton */}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      height: '24px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      width: '60%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: `${index * 0.1 + 0.05}s`,
                    }}
                  />
                  <div
                    style={{
                      height: '18px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      width: '80%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: `${index * 0.1 + 0.1}s`,
                    }}
                  />
                </div>
                {/* Arrow skeleton */}
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#e0e0e0',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: `${index * 0.1 + 0.15}s`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {showSectionTitle && (
        <h3
          style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '24px',
            textAlign: 'center',
          }}
        >
          {sectionTitle}
        </h3>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {categoryConfig.map((category) => {
          const categoryThemes = getThemesByType(category.type);
          const isExpanded = expandedCategory === category.type;
          const hasThemes = categoryThemes.length > 0;

          if (!hasThemes) return null;

          return (
            <div
              key={category.type}
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: isExpanded ? '2px solid #8B5FBF' : '2px solid transparent',
                transition: 'all 0.3s ease',
              }}
            >
              {/* Category Header */}
              <div
                onClick={() => hasThemes && toggleCategory(category.type)}
                style={{
                  padding: '12px 14px',
                  cursor: hasThemes ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  background: isExpanded ? 'linear-gradient(135deg, #FBFAFF, #F5F2FF)' : '#fff',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  if (hasThemes) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #FBFAFF, #F5F2FF)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isExpanded) {
                    e.currentTarget.style.background = '#fff';
                  }
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    background: category.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    flexShrink: 0,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  {category.icon}
                </div>

                {/* Text Content */}
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: '1.2rem',
                      fontWeight: '700',
                      color: '#333',
                      marginBottom: '4px',
                    }}
                  >
                    {category.title}
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '0.95rem',
                      color: '#666',
                    }}
                  >
                    {category.subtitle}
                  </p>
                </div>

                {/* Expand Arrow */}
                {hasThemes && (
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#E9D5FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.3s ease',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      boxShadow: '0 2px 8px rgba(139, 95, 191, 0.15)',
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#8B5FBF"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              {/* Expanded Theme List */}
              {isExpanded && hasThemes && (
                <div
                  style={{
                    padding: '0 8px 12px',
                    backgroundColor: '#FAFAFA',
                    borderTop: '1px solid #eee',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: '6px',
                      paddingTop: '10px',
                    }}
                  >
                    {categoryThemes
                      .sort((a, b) => a.theme_name.localeCompare(b.theme_name))
                      .map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => handleThemeClick(theme)}
                        style={{
                          padding: '0',
                          backgroundColor: '#fff',
                          border: '2px solid #e5e5e5',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#8B5FBF';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 95, 191, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e5e5';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {/* Theme Title */}
                        <div
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '1rem',
                              fontWeight: '600',
                              color: '#333',
                              display: '-webkit-box',
                              WebkitLineClamp: '2',
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: '1.4',
                              minHeight: '1.4em',
                            }}
                          >
                            {theme.theme_name}
                          </span>
                        </div>

                        {/* Content Row: Image and Join Button */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 6px',
                            gap: '10px',
                          }}
                        >
                          {/* Theme Image */}
                          {theme.image_url && (
                            <div
                              style={{
                                width: '130px',
                                minWidth: '130px',
                                height: '75px',
                                overflow: 'hidden',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                              }}
                            >
                              <img
                                src={theme.image_url}
                                alt={theme.theme_name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 1.8rem;">🎯</div>';
                                }}
                              />
                            </div>
                          )}
                          {!theme.image_url && (
                            <div
                              style={{
                                width: '130px',
                                minWidth: '130px',
                                height: '75px',
                                borderRadius: '6px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.8rem',
                              }}
                            >
                              🎯
                            </div>
                          )}

                          {/* Join Button */}
                          <div
                            style={{
                              flex: 1,
                              display: 'flex',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#fff',
                                backgroundColor: '#8B5FBF',
                                padding: '10px 24px',
                                borderRadius: '20px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Join →
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                </div>
              )}

              {/* No themes message */}
              {!hasThemes && (
                <div
                  style={{
                    padding: '0 24px 20px',
                    color: '#999',
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                  }}
                >
                  Coming soon...
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ThemesDropdown;
